/**
 * The ops-log transform layer (spec 5.3): every gesture — single drag, stacked pick,
 * bulk shift, revert at four granularities — is a PURE transform of the working copy
 * of per-slot `individual` override entries. No server call per gesture; the whole
 * log commits only at the page's Calculate.
 *
 * Invariants:
 *  - Only the four date fields are ever appended or stripped here. NON-date override
 *    fields (volume-source etc.) seeded from the committed log ride through untouched
 *    and are always preserved on revert.
 *  - An entry left with an empty `overrides` hash after a strip is dropped.
 *  - Overrides are ISO day strings (any calendar day; no month snapping on user edits).
 */

import moment from 'moment';
import {
  DIALOG_SYNCED_DATE_FIELDS,
  MILESTONE_ORDER,
  MILESTONE_WIRE_FIELD,
} from './constants';
import {
  clamp,
  neighborBounds,
  overlayRowDates,
  parseIsoDay,
  shiftIsoMonths,
  toIsoDay,
} from './utils';
import type {
  BulkShiftResult,
  DateOverrideWrite,
  MergedDate,
  MilestoneKey,
  RevertScope,
  ScheduleOverrideEntry,
  ScheduleRow,
  ScheduleSlotOverrideWire,
  SlotId,
  SlotOverrideRejection,
} from './types';

const DATE_FIELDS: readonly string[] = MILESTONE_ORDER;

/**
 * Appends (or merges into) a single date override for one slot. If the slot already
 * has an entry, the field is merged into its `overrides`; otherwise a new entry is
 * appended. A pure copy — the input array is never mutated.
 *
 * @param {ScheduleOverrideEntry[]} entries - The current working copy.
 * @param {SlotId} slotId - The slot being edited.
 * @param {MilestoneKey} field - The milestone field.
 * @param {string} isoDay - The new ISO-day value.
 * @return {ScheduleOverrideEntry[]} The next working copy.
 */
export function applyDateOverride(
  entries: ScheduleOverrideEntry[],
  slotId: SlotId,
  field: MilestoneKey,
  isoDay: string
): ScheduleOverrideEntry[] {
  const idx = entries.findIndex((e) => e.slotId === slotId);
  if (idx === -1) {
    return [
      ...entries,
      { type: 'individual', slotId, overrides: { [field]: isoDay } },
    ];
  }
  const next = [...entries];
  next[idx] = {
    ...next[idx],
    overrides: { ...next[idx].overrides, [field]: isoDay },
  };
  return next;
}

/**
 * Folds a validated set of CSV date writes into the working copy (spec 2.2). Each write
 * is the SAME log surgery a chart gesture performs — a non-null value appends/merges a
 * date override ({@link applyDateOverride}); a null value strips that milestone back to
 * modeled ({@link revert} at milestone granularity). One implementation of the surgery,
 * shared by the chart edits and the applied upload — no second write path. A pure copy.
 *
 * @param {ScheduleOverrideEntry[]} entries - The current working copy.
 * @param {DateOverrideWrite[]} writes - The validated per-slot date writes (value or null-revert).
 * @return {ScheduleOverrideEntry[]} The next working copy.
 */
export function applyCsvWrites(
  entries: ScheduleOverrideEntry[],
  writes: DateOverrideWrite[]
): ScheduleOverrideEntry[] {
  let next = entries;
  for (const write of writes) {
    next =
      write.value === null
        ? revert(next, {
            kind: 'milestone',
            slotId: write.slotId,
            field: write.field,
          })
        : applyDateOverride(next, write.slotId, write.field, write.value);
  }
  return next;
}

/**
 * The eligibility + application of a relative-month bulk shift over a set of selected
 * points (spec 5.3). Eligibility is per point:
 *  - a locked (reported) point is skipped with reason `reported`;
 *  - a point whose shifted value would break lifecycle order against its slot's
 *    UNSHIFTED neighbors is skipped with reason `outOfOrder`.
 * Entries are appended only for eligible points; the {@link BulkShiftResult} summary
 * reports the applied count + every skip with its reason.
 *
 * @param {ScheduleOverrideEntry[]} entries - The current working copy.
 * @param {ScheduleRow[]} rows - The payload rows (base dates + locked flags).
 * @param {Array} points - Selected points.
 * @param {number} offsetMonths - Signed month offset (negative = earlier).
 * @param {MilestoneKey[]} order - The lifecycle order.
 * @return {object} Next log + summary.
 */
export function applyBulkShift(
  entries: ScheduleOverrideEntry[],
  rows: ScheduleRow[],
  points: Array<{ slotId: SlotId; field: MilestoneKey }>,
  offsetMonths: number,
  order: MilestoneKey[] = MILESTONE_ORDER
): { entries: ScheduleOverrideEntry[]; result: BulkShiftResult } {
  const rowById = new Map<SlotId, ScheduleRow>();
  for (const row of rows) {
    rowById.set(row.slotId, row);
  }

  let next = entries;
  let appliedCount = 0;
  const skipped: BulkShiftResult['skipped'] = [];

  for (const point of points) {
    const row = rowById.get(point.slotId);
    if (!row) {
      continue;
    }
    // Effective dates BEFORE this shift (so a multi-point selection on the same slot
    // still measures order against unshifted neighbors, per spec).
    const dates = overlayRowDates(row, next);
    const merged: MergedDate | undefined = dates[point.field];
    const currentMs = parseIsoDay(merged?.value);

    if (!merged || merged.value === null || currentMs === null) {
      continue;
    }
    if (merged.locked) {
      skipped.push({ ...point, reason: 'reported' });
      continue;
    }

    const nextValue = shiftIsoMonths(merged.value, offsetMonths);
    const nextMs = parseIsoDay(nextValue) as number;
    const { minMs, maxMs } = neighborBounds(dates, point.field, order);
    if (nextMs < minMs || nextMs > maxMs) {
      skipped.push({ ...point, reason: 'outOfOrder' });
      continue;
    }

    next = applyDateOverride(next, point.slotId, point.field, nextValue);
    appliedCount += 1;
  }

  return { entries: next, result: { appliedCount, skipped } };
}

/**
 * Applies a signed day-granular (epoch-ms) delta to a set of selected points as ONE
 * atomic transform — the multi-marker drag (INS-10671). When ≥2 markers are selected and
 * the user grabs one and drags, every selected point shifts by the same delta the grabbed
 * marker moved, so "what the grabbed bar does, the rest do too". Unlike {@link
 * applyBulkShift} (relative months, skip out-of-order), each point here is CLAMPED to its
 * own lifecycle-order neighbor bounds — the same clamp the single drag applies — so the
 * batch is always order-preserving and the calculate never rejects it; there is no skip
 * summary to surface. Per point:
 *  - a missing/null value or a locked (reported) point is skipped (nothing to move);
 *  - the shifted value is clamped into `[minMs, maxMs]` from its UNSHIFTED-in-`next`
 *    neighbors, and a point clamped fully back to its origin contributes no override.
 * `deltaMs` is a whole number of days (both endpoints are UTC midnight). A pure copy —
 * the input array is never mutated.
 *
 * @param {ScheduleOverrideEntry[]} entries - The current working copy.
 * @param {ScheduleRow[]} rows - The payload rows (base dates + locked flags).
 * @param {Array} points - The selected points to shift.
 * @param {number} deltaMs - Signed epoch-ms offset (negative = earlier).
 * @param {MilestoneKey[]} order - The lifecycle order.
 * @return {ScheduleOverrideEntry[]} The next working copy.
 */
export function applyDragShift(
  entries: ScheduleOverrideEntry[],
  rows: ScheduleRow[],
  points: Array<{ slotId: SlotId; field: MilestoneKey }>,
  deltaMs: number,
  order: MilestoneKey[] = MILESTONE_ORDER
): ScheduleOverrideEntry[] {
  if (deltaMs === 0 || points.length === 0) {
    return entries;
  }
  const rowById = new Map<SlotId, ScheduleRow>();
  for (const row of rows) {
    rowById.set(row.slotId, row);
  }

  let next = entries;
  for (const point of points) {
    const row = rowById.get(point.slotId);
    if (!row) {
      continue;
    }
    const dates = overlayRowDates(row, next);
    const merged: MergedDate | undefined = dates[point.field];
    const currentMs = parseIsoDay(merged?.value);
    if (
      !merged ||
      merged.value === null ||
      currentMs === null ||
      merged.locked
    ) {
      continue;
    }

    const { minMs, maxMs } = neighborBounds(dates, point.field, order);
    const clampedMs = clamp(currentMs + deltaMs, minMs, maxMs);
    if (clampedMs === currentMs) {
      continue;
    }
    next = applyDateOverride(
      next,
      point.slotId,
      point.field,
      toIsoDay(clampedMs)
    );
  }
  return next;
}

/**
 * Strips the four DATE fields per a revert scope, preserving every non-date override
 * field, and drops any entry left with an empty `overrides` hash (spec 5.3):
 *  - `milestone` — one field on one slot;
 *  - `slot` — all four date fields on one slot;
 *  - `selection` — the given points;
 *  - `all` — the four date fields on every slot.
 *
 * @param {ScheduleOverrideEntry[]} entries - The current working copy.
 * @param {RevertScope} scope - What to revert.
 * @return {ScheduleOverrideEntry[]} The next working copy.
 */
export function revert(
  entries: ScheduleOverrideEntry[],
  scope: RevertScope
): ScheduleOverrideEntry[] {
  // Which field keys to strip for a given slot (null = leave the slot's entry alone).
  const stripFor = (slotId: SlotId): Set<string> | 'all-dates' | null => {
    switch (scope.kind) {
      case 'milestone':
        return slotId === scope.slotId ? new Set([scope.field]) : null;
      case 'slot':
        return slotId === scope.slotId ? 'all-dates' : null;
      case 'all':
        return 'all-dates';
      case 'selection': {
        const fields = scope.points
          .filter((p) => p.slotId === slotId)
          .map((p) => p.field as string);
        return fields.length ? new Set(fields) : null;
      }
    }
  };

  const next: ScheduleOverrideEntry[] = [];
  for (const entry of entries) {
    const strip = stripFor(entry.slotId);
    if (strip === null) {
      next.push(entry);
      continue;
    }
    const overrides: Record<string, string | null> = {};
    for (const [key, value] of Object.entries(entry.overrides)) {
      const isDate = DATE_FIELDS.includes(key);
      const shouldStrip = isDate && (strip === 'all-dates' || strip.has(key));
      if (!shouldStrip) {
        overrides[key] = value;
      }
    }
    if (Object.keys(overrides).length > 0) {
      next.push({ ...entry, overrides });
    }
  }
  return next;
}

/**
 * How many DATE overrides the working copy currently carries (backs the "Discard N
 * edited dates?" confirm and the edited-count readout). Non-date fields don't count.
 *
 * @param {ScheduleOverrideEntry[]} entries - The working copy.
 * @return {number} The count of edited date fields.
 */
export function editedDateCount(entries: ScheduleOverrideEntry[]): number {
  let count = 0;
  for (const entry of entries) {
    for (const key of Object.keys(entry.overrides)) {
      if (DATE_FIELDS.includes(key) && entry.overrides[key] !== null) {
        count += 1;
      }
    }
  }
  return count;
}

/**
 * Whether the working copy differs from its seeded baseline — the uncommitted-edits
 * flag. Compared structurally (entry order + per-slot override maps), so a revert back
 * to baseline correctly reads as "no edits".
 *
 * @param {ScheduleOverrideEntry[]} working - The working copy.
 * @param {ScheduleOverrideEntry[]} baseline - The committed seed.
 * @return {boolean} True when there are uncommitted edits.
 */
export function hasUncommittedEdits(
  working: ScheduleOverrideEntry[],
  baseline: ScheduleOverrideEntry[]
): boolean {
  return normalize(working) !== normalize(baseline);
}

/**
 * A stable string form of the working copy (slot-sorted, key-sorted) for equality.
 *
 * @param {ScheduleOverrideEntry[]} entries - The working copy.
 * @return {string} The canonical form.
 */
function normalize(entries: ScheduleOverrideEntry[]): string {
  const parts = entries
    .map((e) => {
      const keys = Object.keys(e.overrides)
        .filter((k) => e.overrides[k] !== null)
        .sort();
      const pairs = keys.map((k) => `${k}=${e.overrides[k]}`).join(',');
      return `${e.slotId}:{${pairs}}`;
    })
    .filter((p) => !p.endsWith(':{}'))
    .sort();
  return parts.join(';');
}

/**
 * Serializes the working copy to the calculate wire shape (spec 2.2): `individual`
 * entries with snake_case date field keys (`permit_date` / `spud_date` /
 * `completion_date` / `first_production_date`). Non-date fields pass through as-is.
 * Entries with no effective overrides (all-null) are omitted.
 *
 * @param {ScheduleOverrideEntry[]} entries - The working copy.
 * @return {ScheduleSlotOverrideWire[]} The wire entries.
 */
export function entriesToWire(
  entries: ScheduleOverrideEntry[]
): ScheduleSlotOverrideWire[] {
  const wire: ScheduleSlotOverrideWire[] = [];
  for (const entry of entries) {
    const overrides: Record<string, string | null> = {};
    for (const [key, value] of Object.entries(entry.overrides)) {
      if (value === null) {
        continue;
      }
      const wireKey = MILESTONE_WIRE_FIELD[key as MilestoneKey] ?? key;
      overrides[wireKey] = value;
    }
    if (Object.keys(overrides).length > 0) {
      wire.push({ type: 'individual', slot_id: entry.slotId, overrides });
    }
  }
  return wire;
}

/**
 * An override op the schedule dialog does NOT edit but must preserve verbatim on commit
 * — the eval's `bulk` / `filter` / `ids` ops (INS-10777), which the individual-only
 * schedule edit model cannot represent.
 */
export type PreservedOverrideWire = Record<string, unknown>;

/**
 * The full `slot_overrides` array to commit at Calculate: the preserved (non-individual)
 * ops FIRST, then the schedule's individual date entries LAST so they pin per the
 * engine's later-wins ordering. Merging — not replacing — keeps any bulk/filter/ids ops
 * the schedule dialog does not edit, so a Calculate from the dialog never silently drops
 * the eval's other overrides.
 *
 * @param {PreservedOverrideWire[]} preserved - The non-individual ops to keep verbatim.
 * @param {ScheduleOverrideEntry[]} entries - The working copy of individual date edits.
 * @return {Array} The merged wire ops, in commit order.
 */
export function buildCommitOverrides(
  preserved: PreservedOverrideWire[],
  entries: ScheduleOverrideEntry[]
): PreservedOverrideWire[] {
  return [
    ...preserved,
    ...(entriesToWire(entries) as unknown as PreservedOverrideWire[]),
  ];
}

/** Reverse of {@link MILESTONE_WIRE_FIELD}: a snake wire date key → its camel {@link MilestoneKey}. */
const WIRE_FIELD_MILESTONE: Record<string, MilestoneKey> = Object.fromEntries(
  (Object.entries(MILESTONE_WIRE_FIELD) as Array<[MilestoneKey, string]>).map(
    ([milestone, wire]) => [wire, milestone]
  )
);

/**
 * Splits a committed `slot_overrides` ops-log (the snake_case wire the calc echoes on
 * `configurations.slot_overrides`) into the two seeds the dialog needs — the inverse of
 * {@link buildCommitOverrides}:
 *  - `committed` — the individual DATE ops the schedule surface edits, converted to
 *    {@link ScheduleOverrideEntry} (snake date keys → camel {@link MilestoneKey}). An op
 *    qualifies only when it's `individual`, carries a `slot_id`, and EVERY override key is
 *    one of the four schedule date fields (the exact shape {@link entriesToWire} emits).
 *  - `preserved` — every other op verbatim: bulk / filter / ids ops AND individual
 *    ECONOMIC ops (the camelCase fields the slot table owns, whose values may be numeric),
 *    so a schedule-dialog Calculate re-appends them and never drops the eval's other edits.
 *
 * ⚠️ AN UNSET IS FOLDED, NOT JUST PRESERVED. An `unset` is not
 * an individual op, so it went to `preserved` while the set it reverted stayed `committed`
 * — and {@link buildCommitOverrides} emits preserved FIRST and committed LAST, so the
 * re-emitted set followed the unset and the revert was undone at the next Calculate. The
 * reverted date was also seeded back as the dialog's working edit. The log is therefore
 * folded in order, as the resolver folds it: an unset strips the date fields it names from
 * the committed entries BEFORE it, and a set after it re-records them. The unset itself
 * still rides through verbatim, so the non-date fields it names — and any preserved op it
 * reverted — stay reverted in their original relative order.
 *
 * Pure — the input array is never mutated.
 *
 * @param {unknown[]} [ops] - The committed ops-log (undefined pre-run).
 * @return {object} The two dialog seeds — `{ committed, preserved }`.
 */
export function splitCommittedOverrides(ops: readonly unknown[] | undefined): {
  committed: ScheduleOverrideEntry[];
  preserved: PreservedOverrideWire[];
} {
  let committed: ScheduleOverrideEntry[] = [];
  const preserved: PreservedOverrideWire[] = [];
  for (const raw of ops ?? []) {
    if (typeof raw !== 'object' || raw === null) {
      continue;
    }
    const op = raw as PreservedOverrideWire;
    const entry = toScheduleDateEntry(op);
    if (entry) {
      committed.push(entry);
    } else {
      if (op['type'] === 'unset') {
        committed = forgetUnsetDates(committed, op);
      }
      preserved.push(op);
    }
  }
  return { committed, preserved };
}

/**
 * Strips the date fields one committed `unset` names from the date entries before it (see
 * {@link splitCommittedOverrides}). An entry left with no date is dropped, as a revert strip
 * drops one (module invariants).
 *
 * The slots come from `slot_ids` when present, else the singular `slot_id` — the resolver's
 * `unset_target_ids` rule, since a revert of a bulk edit names several. A field is matched in
 * either spelling the engine's `canonical_field` treats as one: the snake wire key the dialog
 * emits, or the camelCase `OverrideFields` key the grid and tearsheet revert with. Any other
 * field is not a date and is left to the verbatim unset.
 *
 * @param {ScheduleOverrideEntry[]} committed - The date entries folded so far.
 * @param {PreservedOverrideWire} unset - One committed `unset` op.
 * @return {ScheduleOverrideEntry[]} The entries with the named dates removed.
 */
function forgetUnsetDates(
  committed: ScheduleOverrideEntry[],
  unset: PreservedOverrideWire
): ScheduleOverrideEntry[] {
  const slotIds = Array.isArray(unset['slot_ids'])
    ? (unset['slot_ids'] as unknown[])
    : [unset['slot_id']];
  const fields = Array.isArray(unset['fields'])
    ? (unset['fields'] as unknown[])
    : [];
  const milestones = fields
    .filter((field): field is string => typeof field === 'string')
    .map(
      (field) => WIRE_FIELD_MILESTONE[field] ?? DIALOG_SYNCED_DATE_FIELDS[field]
    )
    .filter((milestone): milestone is MilestoneKey => milestone !== undefined);
  if (milestones.length === 0) {
    return committed;
  }
  return committed
    .map((entry) => {
      if (!slotIds.includes(entry.slotId)) {
        return entry;
      }
      const overrides = { ...entry.overrides };
      for (const milestone of milestones) {
        delete overrides[milestone];
      }
      return { ...entry, overrides };
    })
    .filter((entry) => Object.keys(entry.overrides).length > 0);
}

/**
 * Converts one committed op to a schedule date entry, or `null` when it is not a
 * pure-date individual op (see {@link splitCommittedOverrides}). Bracket access reads the
 * snake wire keys off the untyped op without tripping the camelCase identifier rule.
 *
 * @param {PreservedOverrideWire} op - One committed override op.
 * @return {ScheduleOverrideEntry | null} The date entry, or null to preserve the op verbatim.
 */
function toScheduleDateEntry(
  op: PreservedOverrideWire
): ScheduleOverrideEntry | null {
  const slotId = op['slot_id'];
  const wireOverrides = op['overrides'];
  if (
    op['type'] !== 'individual' ||
    typeof slotId !== 'string' ||
    typeof wireOverrides !== 'object' ||
    wireOverrides === null
  ) {
    return null;
  }
  const keys = Object.keys(wireOverrides);
  if (
    keys.length === 0 ||
    !keys.every((key) => WIRE_FIELD_MILESTONE[key] !== undefined)
  ) {
    return null;
  }
  const source = wireOverrides as Record<string, string | null>;
  const overrides: Record<string, string | null> = {};
  for (const key of keys) {
    overrides[WIRE_FIELD_MILESTONE[key]] = source[key];
  }
  return { type: 'individual', slotId, overrides };
}

// ---------------------------------------------------------------------------
// Calculate-time 422 rejection parsing (spec 2.2 / AC 7)
// ---------------------------------------------------------------------------

/**
 * A calculate rejected by the locked/order acceptance check, with per-entry detail.
 * Thrown by the eval page's `useCalculate` when the unified Calculate's `422` carries
 * a `{ errors: { slot_overrides: [...] } }` body, so a locked/out-of-order schedule
 * date edit still surfaces to the user (the dialog no longer runs its own calculate).
 */
/**
 * The milestone names as they read in a SENTENCE. {@link MILESTONE_LABELS} is the chart's
 * vocabulary — terse, because a gantt marker has no room for more — and "Completion
 * 03/15/2027" reads as a fragment in prose. Keyed by MilestoneKey so a milestone cannot be
 * added to the lifecycle and missed here.
 */
const MILESTONE_PROSE: Record<MilestoneKey, string> = {
  permitDate: 'Permit Date',
  spudDate: 'Spud Date',
  modeledCompletionDate: 'Completion Date',
  firstProductionDate: 'First Production',
};

/**
 * The table's day format, as the gantt's own marker labels use it.
 *
 * @param {string} iso - The ISO day.
 * @return {string} The day as MM/DD/YYYY.
 */
const day = (iso: string): string => moment(iso).format('MM/DD/YYYY');

/**
 * A wire field name (`spud_date`) in prose, falling back to the raw name.
 *
 * @param {string} [field] - The snake_case wire field.
 * @return {string} The milestone in sentence form.
 */
function fieldProse(field: string | undefined): string {
  const milestone = field ? WIRE_FIELD_MILESTONE[field] : undefined;
  return milestone ? MILESTONE_PROSE[milestone] : (field ?? 'that milestone');
}

/**
 * One rejection in words — which field, which value, WHICH RULE, and what would be accepted.
 *
 * ⚠️ NEVER "or". The backend has always distinguished the two rules by `code`; the old copy
 * threw that away and offered both, so a user had to guess which one they had hit and what
 * to change. Each branch here names exactly one.
 *
 * A bound is omitted when the slot has no dated neighbour on that side — the validator drops
 * it rather than sending null — so a milestone constrained on one side reads as a one-sided
 * sentence instead of claiming a limit that does not exist.
 *
 * @param {SlotOverrideRejection} rejection - The rejection to describe.
 * @return {string} A single sentence.
 */
export function describeRejection(rejection: SlotOverrideRejection): string {
  const name = fieldProse(rejection.field);
  if (rejection.code === 'locked_reported') {
    return `${name} is an actual reported by the well and cannot be overridden.`;
  }
  // ── ⚠️ A DATE THAT IS NOT A DATE ──────────────────────────────────────────────────────
  //
  // The server now refuses an unparseable value instead of persisting it
  // (`OverrideWriteValidator::INVALID_DATE`). It carries `attempted` and NO bounds, because
  // there is no date to compare — so this cannot fall through to the sentence below, which
  // would say "must be on or after …" about a value that is not on any axis.
  //
  // ⚠️ AND `parseRejections` HAD TO LEARN IT TOO. It accepts a fixed set of codes and drops
  // anything else, so a third code emitted server-side and not listed there would be a
  // refusal the user never sees — the request 422s and the page says nothing.
  // An AFE id with no AFE behind it: it carries the id and no bounds, and names no
  // milestone, so it has a sentence of its own.
  if (rejection.code === 'unknown_afe') {
    const id = rejection.attempted ? ` ${rejection.attempted}` : '';
    return `AFE${id} does not exist, so it cannot be applied.`;
  }
  if (rejection.code === 'invalid_date') {
    const tried = rejection.attempted ? ` "${rejection.attempted}"` : '';
    return `${name}${tried} is not a date.`;
  }
  const attempted = rejection.attempted ? ` ${day(rejection.attempted)}` : '';
  const lower = rejection.min
    ? `on or after ${fieldProse(rejection.minField)} ${day(rejection.min)}`
    : null;
  const upper = rejection.max
    ? `on or before ${fieldProse(rejection.maxField)} ${day(rejection.max)}`
    : null;
  const bounds = [lower, upper].filter(Boolean).join(' and ');
  return bounds
    ? `${name}${attempted} must be ${bounds}.`
    : `${name}${attempted} breaks permit → spud → completion → first production order.`;
}

/** How many rejections are spelled out before the rest are counted instead. */
const REJECTIONS_SPELLED_OUT = 3;

/**
 * The banner text for a rejected Calculate. Spells out the first few and counts the rest —
 * a user fixes them one at a time, and a wall of sentences is no more actionable than the
 * single vague one this replaced.
 *
 * @param {SlotOverrideRejection[]} rejections - The rejections from the 422.
 * @return {string} The message.
 */
export function rejectionMessage(rejections: SlotOverrideRejection[]): string {
  const shown = rejections
    .slice(0, REJECTIONS_SPELLED_OUT)
    .map(describeRejection);
  const remaining = rejections.length - shown.length;
  const more =
    remaining > 0 ? ` ${remaining} more edit(s) were also rejected.` : '';
  return `${shown.join(' ')}${more} Your changes are kept — try again.`;
}

export class ScheduleCalculateRejectedError extends Error {
  readonly rejections: SlotOverrideRejection[];

  constructor(rejections: SlotOverrideRejection[]) {
    super(rejectionMessage(rejections));
    this.rejections = rejections;
    this.name = 'ScheduleCalculateRejectedError';
  }
}

/**
 * Parses the `422` `{ errors: { slot_overrides: [...] } }` body into typed rejections.
 * Tolerant of casing drift: Rails 422 validation bodies can render outside Jbuilder's
 * camelization, so both `slotId`/`slot_id` and `field`/`slot_field` are accepted — a
 * casing mismatch degrades to a shown banner, never a silently swallowed rejection.
 *
 * @param {unknown} body - The parsed error body.
 * @return {SlotOverrideRejection[]} The rejections (empty when the body has none).
 */
export function parseRejections(body: unknown): SlotOverrideRejection[] {
  const raw = (body as { errors?: { slot_overrides?: unknown[] } } | null)
    ?.errors?.slot_overrides;
  if (!Array.isArray(raw)) {
    return [];
  }
  const rejections: SlotOverrideRejection[] = [];
  for (const item of raw) {
    const entry = item as Record<string, unknown>;
    const slotId = entry.slotId ?? entry.slot_id;
    const field = entry.field ?? entry.slot_field;
    const { code } = entry;
    if (
      typeof slotId === 'string' &&
      typeof field === 'string' &&
      (code === 'locked_reported' ||
        code === 'out_of_order' ||
        code === 'invalid_date' ||
        code === 'unknown_afe')
    ) {
      const str = (v: unknown): string | undefined => {
        return typeof v === 'string' && v.length > 0 ? v : undefined;
      };
      rejections.push({
        slotId,
        field,
        code,
        attempted: str(entry.attempted),
        min: str(entry.min),
        minField: str(entry.minField ?? entry.min_field),
        max: str(entry.max),
        maxField: str(entry.maxField ?? entry.max_field),
      });
    }
  }
  return rejections;
}
